import type { MaybeRefOrGetter } from 'vue'
import { type JsonLdNode, pruneJsonLd } from '@/utils/structuredData'

type JsonLdInput = JsonLdNode | null | undefined | false

/**
 * Renders schema.org JSON-LD blocks into the page head (SSR included).
 *
 * Pass a getter so the blocks follow the page data; falsy entries are skipped, which keeps
 * "only when the item is public" checks next to the builder. Each block is keyed by its
 * `@type`, so a type appears at most once per page. Empty properties are pruned, and
 * Unhead escapes `<` inside JSON scripts, so user text can't close the script tag.
 */
export function useJsonLd(blocks: MaybeRefOrGetter<JsonLdInput[]>) {
    useHead(() => ({
        script: toValue(blocks)
            .map((block) => (block ? pruneJsonLd(block) : undefined))
            .filter((block): block is JsonLdNode => !!block)
            .map((block) => ({
                key: `ld-json-${String(block['@type']).toLowerCase()}`,
                type: 'application/ld+json',
                innerHTML: JSON.stringify({ '@context': 'https://schema.org', ...block })
            }))
    }))
}
